/**
 * gateExport.js — exporta um Quality Gate (qualquer um dos 5) em Markdown e PDF.
 *
 * Reusa os utilitários já existentes de `../analysisExport.js` (downloadFile,
 * slugify, mdToHtml) — sem depender de nenhuma nova chamada de API: o objeto
 * `gate` é montado por cada tela de gate a partir dos dados JÁ carregados
 * na tela (ver GateExportBar.jsx e o shape abaixo).
 *
 * Shape esperado de `gate` (todos os campos são opcionais — seções ausentes
 * são omitidas graciosamente):
 *   {
 *     num: number,                 // 1..5
 *     label: string,               // "Escopo de Negócio" etc.
 *     projectName?: string,
 *     projectId?: string,
 *     description?: string,        // subtítulo/objetivo do gate (1 parágrafo)
 *     status?: string,             // 'approved' | 'rejected' | 'pending' | ...
 *     statusLabel?: string,        // rótulo legível (ex.: "APROVADO")
 *     decidedAt?: string,          // ISO datetime
 *     comment?: string,            // parecer do aprovador
 *     sla?: string,                // ex.: "24h"
 *     personas?: string,           // ex.: "Product Owner · Product Manager"
 *     approvers?: Array<{ name, role, side?, done? }>,
 *     sections?: Array<
 *       | { title: string, kind: 'list',  items: string[] }
 *       | { title: string, kind: 'table', headers: string[], rows: (string|number)[][] }
 *       | { title: string, kind: 'kv',    data: [string, string|number][] }
 *       | { title: string, kind: 'text',  text: string }   // markdown livre
 *     >,
 *   }
 */
import { downloadFile, slugify, mdToHtml } from '../analysisExport.js';

// ── helpers de formatação markdown ────────────────────────────────────────────

function esc(v) {
  return String(v ?? '').replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');
}

function mdTable(headers, rows) {
  if (!rows?.length) return '';
  const head = `| ${headers.map(esc).join(' | ')} |`;
  const sep = `| ${headers.map(() => '---').join(' | ')} |`;
  const body = rows.map((r) => `| ${r.map(esc).join(' | ')} |`).join('\n');
  return `${head}\n${sep}\n${body}`;
}

function mdList(items) {
  return (items || []).filter((x) => x != null && String(x).trim()).map((x) => `- ${x}`).join('\n');
}

function mdKv(pairs) {
  return (pairs || [])
    .filter(([, v]) => v != null && String(v).trim())
    .map(([k, v]) => `- **${k}:** ${v}`)
    .join('\n');
}

function renderSection(section) {
  const { title, kind, items, headers, rows, data, text } = section || {};
  let body = '';
  if (kind === 'list') body = mdList(items);
  else if (kind === 'table') body = mdTable(headers || [], rows || []);
  else if (kind === 'kv') body = mdKv(data || []);
  else if (kind === 'text') body = String(text || '').trim();
  if (!body) return '';
  return `\n\n## ${title}\n\n${body}`;
}

// ── construção do markdown ────────────────────────────────────────────────────

/** buildGateMarkdown — monta o Markdown completo do gate. */
export function buildGateMarkdown(gate) {
  const g = gate || {};
  const now = new Date().toLocaleString('pt-BR');
  const lines = [];

  lines.push(`# Quality Gate ${g.num ?? '—'} — ${g.label ?? ''}`.trim());
  lines.push('');
  lines.push(`_Gerado pelo Nava Flow em ${now}._`);

  const meta = [];
  if (g.projectName || g.projectId) meta.push(['Projeto', g.projectName || g.projectId]);
  if (g.statusLabel || g.status) meta.push(['Status', g.statusLabel || g.status]);
  if (g.sla) meta.push(['SLA', g.sla]);
  if (g.personas) meta.push(['Personas aprovadoras', g.personas]);
  if (g.decidedAt) {
    meta.push(['Decidido em', new Date(g.decidedAt).toLocaleString('pt-BR')]);
  }
  if (meta.length) {
    lines.push('');
    lines.push(mdKv(meta));
  }

  if (g.description) {
    lines.push('');
    lines.push(String(g.description));
  }

  if (Array.isArray(g.approvers) && g.approvers.length) {
    lines.push('\n\n## Aprovadores');
    lines.push('');
    lines.push(mdTable(
      ['Nome', 'Papel', 'Lado', 'Status'],
      g.approvers.map((a) => [
        a.name || '—',
        a.role || '—',
        a.side || '—',
        a.done ? 'Aceito ✓' : 'Pendente',
      ]),
    ));
  }

  if (g.comment) {
    lines.push('\n\n## Parecer do aprovador');
    lines.push('');
    lines.push(String(g.comment));
  }

  (g.sections || []).forEach((section) => {
    const rendered = renderSection(section);
    if (rendered) lines.push(rendered);
  });

  return lines.join('\n').trim() + '\n';
}

/** downloadGateMarkdown — baixa o .md do gate (nome: gate-<num>-<slug>.md). */
export function downloadGateMarkdown(gate) {
  const g = gate || {};
  const name = `gate-${g.num ?? 'x'}-${slugify(g.label || 'gate')}.md`;
  downloadFile(name, buildGateMarkdown(g), 'text/markdown');
}

// ── PDF (via impressão do navegador) ──────────────────────────────────────────

const PRINT_STYLE = `
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  body { margin:0; background:#FFFFFF; color:#141414; font:14px/1.6 -apple-system,Segoe UI,Roboto,sans-serif; }
  header.brand { display:flex; align-items:center; gap:10px; padding:20px 36px 14px;
    border-bottom:2px solid #01E6E4; }
  header.brand .mark { font-weight:800; font-size:18px; letter-spacing:-0.02em; color:#0C0C0C; }
  header.brand .mark span { color:#01E6E4; }
  header.brand .meta { margin-left:auto; font-size:11.5px; color:#6B6B66; text-align:right; }
  main { padding:24px 36px 60px; max-width:900px; margin:0 auto; }
  h1 { font-size:22px; letter-spacing:-0.02em; margin:18px 0 6px; color:#0C0C0C; }
  h2 { font-size:16px; margin-top:22px; margin-bottom:8px; color:#0C0C0C;
    border-left:3px solid #01E6E4; padding-left:9px; }
  h3 { font-size:13.5px; color:#3A3A38; }
  p { margin:8px 0; }
  code { background:#F1F1EE; padding:1px 6px; border-radius:5px;
    font-family:ui-monospace,SFMono-Regular,Menlo,monospace; font-size:0.9em; color:#0F7A79; }
  pre { background:#F7F7F4; border:1px solid #DDDBD3; border-radius:8px; padding:12px; overflow:auto; }
  table { border-collapse:collapse; width:100%; margin:10px 0; font-size:12.5px; }
  th, td { border:1px solid #DDDBD3; padding:6px 10px; text-align:left; }
  th { background:#F1F1EE; color:#0C0C0C; }
  ul, ol { padding-left:20px; }
  hr { border:none; border-top:1px solid #DDDBD3; margin:18px 0; }
  strong { color:#0C0C0C; }
  @media print {
    header.brand { position:running(head); }
    a[href]:after { content: none !important; }
  }
`;

function buildGatePrintHtml(gate) {
  const g = gate || {};
  const md = buildGateMarkdown(g);
  const body = mdToHtml(md.replace(/^# .*\n/, '')); // o título vira <h1> no header
  const title = `Quality Gate ${g.num ?? ''} — ${g.label ?? ''}`.trim();
  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8"/>
<title>${title} — Nava Flow</title>
<style>${PRINT_STYLE}</style></head>
<body>
  <header class="brand">
    <span class="mark">nava<span>·</span>flow</span>
    <span class="meta">${title}${g.projectName ? ` · ${g.projectName}` : ''}<br/>${new Date().toLocaleString('pt-BR')}</span>
  </header>
  <main>
    <h1>${title}</h1>
    ${body}
  </main>
</body></html>`;
}

/**
 * printGatePdf — abre uma janela com o gate formatado para impressão e
 * chama window.print() (o usuário salva como PDF pelo diálogo do navegador).
 * Trata popup bloqueado com um alerta amigável em pt-BR.
 */
export function printGatePdf(gate) {
  const html = buildGatePrintHtml(gate);
  const win = window.open('', '_blank', 'noopener,noreferrer,width=900,height=1000');
  if (!win) {
    window.alert(
      'Não foi possível abrir a janela de impressão. Verifique se o navegador '
      + 'está bloqueando pop-ups para este site e tente novamente.',
    );
    return false;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  const doPrint = () => {
    try { win.focus(); win.print(); } catch { /* janela pode já ter sido fechada */ }
  };
  win.onload = doPrint;
  // Fallback: alguns navegadores não disparam onload após document.write().
  setTimeout(doPrint, 300);
  return true;
}
